import {
  split,
  HttpLink,
  ApolloClient,
  ApolloLink,
  InMemoryCache
} from "@apollo/client";
// import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { Client, createClient } from "graphql-ws";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GRAPHQL_API_URL, SUBSCRIPTION_GRAPHQL_URL } from "config/config";
import { ErrorTypes } from "../constants";
import { GATEWAY_GRAPHQL_URL } from "./endpoints";

import { ensureValidAccessToken } from "./token-refresh";
import {
  reportNetworkFailure,
  reportNetworkSuccess
} from "./connectivity-state";

export const getGraphQLClient = ({
  token,
  getAuthHeaders
}: {
  token?: any;
  getAuthHeaders?: () => Promise<{ Authorization: string }>;
}) => {
  // Dual-token architecture: the subscription client passes the coordinator
  // (getAuthHeaders) so its token is renewed reactively and never stale; the
  // project client passes its own project-scoped token (needs x-hasura-project-id
  // claims), which the coordinator does not manage.
  const resolveAuthHeaders =
    getAuthHeaders ??
    (async () => ({ Authorization: token ? `Bearer ${token}` : "" }));

  const subscriptionClient: Client = createClient({
    url: SUBSCRIPTION_GRAPHQL_URL,
    connectionParams: async () => {
      return {
        headers: await resolveAuthHeaders()
      };
    }
  });

  subscriptionClient.on("closed", (eventClosedParams: any) => {
    if (eventClosedParams?.reason?.includes("JWTExpired")) {
      // Refresh and let graphql-ws reconnect with the new token, instead of
      // ejecting. ensureValidAccessToken logs out only if the refresh fails.
      void ensureValidAccessToken();
    }
  });

  const wsLink = new GraphQLWsLink(subscriptionClient);

  const queryLink = new HttpLink({
    uri: (params: any) => {
      const operation = params?.operationName ?? "unknown";
      return `${GRAPHQL_API_URL}?op=${operation}`;
    }
  });

  const mutationLink = new HttpLink({
    uri: (params: any) => {
      const operation = params?.operationName ?? "unknown";
      return `${GATEWAY_GRAPHQL_URL}?op=${operation}`;
    }
  });

  const httpLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        (definition.kind as string) === "OperationDefinition" &&
        (definition as any).operation === "query"
      );
    },
    queryLink,
    mutationLink
  );

  // The split function takes three parameters:
  // * A function that's called for each operation to execute
  // * The Link to use for an operation if the function returns a "truthy" value
  // * The Link to use for an operation if the function returns a "falsy" value
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        (definition.kind as string) === "OperationDefinition" &&
        (definition as any).operation === "subscription"
      );
    },
    wsLink,
    httpLink
  );

  const authLink = setContext(async (_, { headers }) => {
    // Resolve auth per request so a long-lived client never sends a stale
    // token (the subscription client renews via the coordinator).
    return {
      headers: {
        ...headers,
        ...(await resolveAuthHeaders())
      }
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach((error) => {
        const { extensions } = error;
        if (extensions?.code === "invalid-jwt") {
          // Refresh instead of ejecting; logout happens only if the refresh
          // itself fails (handled inside the coordinator).
          void ensureValidAccessToken();
        }
      });
      try {
        console.error(
          "[GraphQL errors]",
          JSON.stringify(graphQLErrors, null, 2)
        );
      } catch {
        console.error("[GraphQL errors] (non-stringifiable)", graphQLErrors);
      }
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
      // Feed the connectivity heartbeat — only genuine network-shaped
      // failures count (teardown cancels etc. must not fake an outage).
      if (ErrorTypes.netWorkError(String((networkError as any)?.message))) {
        reportNetworkFailure();
      }
    }
  });

  // Every operation that completes proves the backend is reachable — with the
  // 1s audit poll always running, this is a free connectivity probe.
  const connectivityTap = new ApolloLink((operation, forward) =>
    forward(operation).map((result) => {
      reportNetworkSuccess();
      return result;
    })
  );

  const GQLClient = new ApolloClient({
    link: authLink.concat(errorLink).concat(connectivityTap).concat(splitLink),
    cache: new InMemoryCache(),
    defaultOptions: {
      query: {
        // fetchPolicy: "no-cache",
        errorPolicy: "all"
      },
      watchQuery: {
        // fetchPolicy: "no-cache",
        errorPolicy: "all"
      },
      mutate: {
        errorPolicy: "all"
      }
    }
  });

  return { GQLClient, wsLink };
};
